import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [lockoutMessage, setLockoutMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Please enter email and password'); return; }
    setLockoutMessage('');
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/admin');
    } catch (err) {
      const backendMessage = err.response?.data?.detail || '';
      if (err.response?.status === 429 && backendMessage) {
        setLockoutMessage(backendMessage);
        toast.error(backendMessage);
      } else {
        toast.error('Invalid email or password');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-[0_8px_30px_rgb(30,79,161,0.06)] p-8">
          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-[#1E4FA1] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock size={24} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Admin Login
            </h1>
            <p className="mt-1 text-[#94A3B8] text-sm">Sign in to manage your ministry website</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {lockoutMessage && (
              <div
                data-testid="admin-login-lockout-message"
                className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900"
              >
                {lockoutMessage}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Email</label>
              <Input
                data-testid="admin-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.org"
                className="rounded-xl h-11"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Password</label>
              <Input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="rounded-xl h-11"
              />
            </div>
            <Button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full bg-[#1E4FA1] hover:bg-[#153A7A] text-white rounded-xl h-11 font-medium"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
